import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, RotateCcw } from "lucide-react";
import "./profile-photo.css";

// Coordinates use the browser-decoded image, including EXIF orientation.
export default function PhotoCropDialog({ file, onCancel, onConfirm }) {
  const [source, setSource] = useState(null);
  const [error, setError] = useState("");
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0.5, y: 0.5 });
  const [busy, setBusy] = useState(false);
  const drag = useRef(null);
  const saving = useRef(false);
  useEffect(() => {
    if (!file) return;
    let cancelled = false;
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { if (!cancelled) setSource({ url, width: img.naturalWidth, height: img.naturalHeight }); };
    img.onerror = () => { if (!cancelled) setError("Foto tidak dapat dibaca. Pilih foto JPG, PNG, atau WebP lainnya."); };
    img.src = url;
    return () => { cancelled = true; URL.revokeObjectURL(url); };
  }, [file]);
  const size = source ? Math.min(source.width, source.height) / zoom : 1;
  const rangeX = source ? source.width - size : 0;
  const rangeY = source ? source.height - size : 0;
  const crop = { x: position.x * rangeX, y: position.y * rangeY, size };
  const clamp = v => Math.max(0, Math.min(1, v));
  const submit = async () => {
    if (!source || saving.current) return;
    saving.current = true; setBusy(true);
    try { if (await onConfirm(crop) !== false) onCancel(); }
    finally { saving.current = false; setBusy(false); }
  };
  return <Dialog open={!!file} onOpenChange={open => { if (!open && !saving.current) onCancel(); }}>
    <DialogContent className="eh-photo-dialog" data-testid="photo-crop-dialog" onPointerDownOutside={event => event.preventDefault()}>
      <DialogTitle>Atur foto profil</DialogTitle>
      <DialogDescription>Geser dan perbesar foto agar wajah berada di tengah. Foto otomatis dikompres saat disimpan.</DialogDescription>
      {error ? <p role="alert">{error}</p> : !source ? <p role="status">Membuka foto…</p> : <>
        <div className="eh-photo-crop" data-testid="photo-crop-preview" tabIndex={0} role="group" aria-label="Geser foto; tombol panah juga dapat digunakan"
          onPointerDown={event => {
            if (busy || !event.isPrimary) return;
            event.currentTarget.setPointerCapture(event.pointerId);
            drag.current = { x: event.clientX, y: event.clientY, cropX: crop.x, cropY: crop.y, width: event.currentTarget.getBoundingClientRect().width };
          }}
          onPointerMove={event => {
            if (!drag.current || busy) return;
            const origin = drag.current;
            setPosition({ x: rangeX ? clamp((origin.cropX - (event.clientX - origin.x) * size / origin.width) / rangeX) : 0.5,
              y: rangeY ? clamp((origin.cropY - (event.clientY - origin.y) * size / origin.width) / rangeY) : 0.5 });
          }} onPointerUp={() => { drag.current = null; }} onPointerCancel={() => { drag.current = null; }} onLostPointerCapture={() => { drag.current = null; }}
          onKeyDown={event => {
            if (busy || !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
            event.preventDefault();
            setPosition(p => ({ x: clamp(p.x + (event.key === "ArrowLeft" ? -0.05 : event.key === "ArrowRight" ? 0.05 : 0)), y: clamp(p.y + (event.key === "ArrowUp" ? -0.05 : event.key === "ArrowDown" ? 0.05 : 0)) }));
          }}>
          <img src={source.url} alt="Pratinjau potongan foto" draggable={false} style={{ width: `${source.width / size * 100}%`, height: `${source.height / size * 100}%`, left: `${-crop.x / size * 100}%`, top: `${-crop.y / size * 100}%` }}/>
          <span className="eh-photo-guide" aria-hidden="true"/>
        </div>
        <label className="eh-photo-zoom">Perbesar <span>{Math.round(zoom * 100)}%</span>
          <input aria-label="Perbesar foto" type="range" min="1" max="3" step="0.01" value={zoom} disabled={busy} onChange={e => setZoom(Number(e.target.value))}/>
        </label>
        <button className="eh-photo-reset" type="button" disabled={busy} onClick={() => { setZoom(1); setPosition({ x: 0.5, y: 0.5 }); }}><RotateCcw size={14}/> Atur ulang posisi</button>
      </>}
      <div className="eh-photo-actions"><button type="button" disabled={busy} onClick={onCancel}>Batal</button><button type="button" className="primary" disabled={!source || busy || !!error} onClick={submit} data-testid="photo-crop-save">{busy && <Loader2 size={16} className="animate-spin"/>}{busy ? "Menyimpan…" : "Simpan foto"}</button></div>
    </DialogContent>
  </Dialog>;
}
